/**
 * StickyCTABar — Bottom-fixed conversion bar for mobile/desktop
 * Appears after scroll, high-converting CTA with urgency signals
 * Only shown on services and pricing pages (not careers, about, contact)
 */
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Phone } from 'lucide-react';
import BRAND from '@/config/brand';

const CTA_TEXT: Record<string, { label: string; sub: string; phone: string }> = {
  en: { label: 'Get Your Free Quote', sub: '50-70% savings • Deploy in 7 days', phone: 'Call Now' },
  fr: { label: 'Devis Gratuit', sub: '50-70% économies • Déploiement 7 jours', phone: 'Appelez' },
  es: { label: 'Cotización Gratis', sub: '50-70% ahorro • Despliegue 7 días', phone: 'Llame' },
  ar: { label: 'عرض أسعار مجاني', sub: 'توفير 50-70% • نشر في 7 أيام', phone: 'اتصل' },
  de: { label: 'Kostenloses Angebot', sub: '50-70% Einsparungen • 7 Tage Bereitstellung', phone: 'Anrufen' },
  it: { label: 'Preventivo Gratuito', sub: '50-70% risparmio • Deploy in 7 giorni', phone: 'Chiama' },
  pt: { label: 'Orçamento Grátis', sub: '50-70% economia • Implantação 7 dias', phone: 'Ligue' },
  nl: { label: 'Gratis Offerte', sub: '50-70% besparing • 7 dagen oplevering', phone: 'Bel Nu' },
  pl: { label: 'Darmowa Wycena', sub: '50-70% oszczędności • Wdrożenie 7 dni', phone: 'Zadzwoń' },
  ru: { label: 'Бесплатная Оценка', sub: '50-70% экономия • Запуск за 7 дней', phone: 'Звоните' },
  zh: { label: '免费报价', sub: '节省50-70% • 7天部署', phone: '致电' },
  ja: { label: '無料見積もり', sub: '50-70%コスト削減 • 7日導入', phone: '電話' },
  tr: { label: 'Ücretsiz Teklif', sub: '%50-70 tasarruf • 7 günde kurulum', phone: 'Arayın' },
  ko: { label: '무료 견적', sub: '50-70% 절감 • 7일 배포', phone: '전화' },
  sv: { label: 'Gratis Offert', sub: '50-70% besparingar • 7 dagars driftsättning', phone: 'Ring' },
  da: { label: 'Gratis Tilbud', sub: '50-70% besparelser • 7 dages udrulning', phone: 'Ring' },
  hi: { label: 'मुफ्त कोटेशन पाएं', sub: '50-70% बचत • 7 दिन में तैनात', phone: 'कॉल करें' },
  ro: { label: 'Ofertă Gratuită', sub: '50-70% economii • Implementare 7 zile', phone: 'Sunați' },
  cs: { label: 'Bezplatná Nabídka', sub: '50-70% úspory • Nasazení za 7 dní', phone: 'Volejte' },
  hu: { label: 'Ingyenes Árajánlat', sub: '50-70% megtakarítás • 7 napos telepítés', phone: 'Hívjon' },
  fi: { label: 'Ilmainen Tarjous', sub: '50-70% säästöt • Käyttöönotto 7 päivässä', phone: 'Soita' },
};

// Pages where StickyCTABar should NOT appear
const HIDDEN_ON_SEGMENTS = ['careers', 'about', 'contact', 'admin', 'portal', 'jobs', 'legal', 'whistleblowing'];

export function StickyCTABar() {
  const { language } = useLanguage();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  // Check if current page should show the bar
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const shouldShow = !pathSegments.some(seg => HIDDEN_ON_SEGMENTS.includes(seg));

  useEffect(() => {
    if (!shouldShow) return;
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [shouldShow]);

  const t = CTA_TEXT[language] || CTA_TEXT.en;

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-cyan to-cyan/90 shadow-[0_-4px_20px_rgba(0,0,0,0.3)]"
        >
          {/* Mobile layout */}
          <div className="md:hidden px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-navy font-bold text-sm truncate">{t.label}</p>
              <p className="text-navy/70 text-xs truncate">{t.sub}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={`tel:${BRAND.phone.main.replace(/\s/g, '')}`}
                className="p-2.5 bg-navy/10 rounded-full hover:bg-navy/20 transition-colors"
                aria-label={t.phone}
              >
                <Phone className="w-4 h-4 text-navy" />
              </a>
              <a
                href={`/${language}/contact`}
                className="flex items-center gap-1.5 bg-navy text-cyan px-4 py-2.5 rounded-full font-semibold text-sm hover:bg-navy/90 transition-colors"
              >
                {t.label}
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Desktop layout */}
          <div className="hidden md:flex items-center justify-center gap-6 px-6 py-2.5">
            <p className="text-navy font-bold text-sm">{t.sub}</p>
            <a
              href={`/${language}/contact`}
              className="flex items-center gap-2 bg-navy text-cyan px-6 py-2 rounded-full font-semibold text-sm hover:bg-navy/90 transition-all hover:scale-105"
            >
              {t.label}
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={`tel:${BRAND.phone.main.replace(/\s/g, '')}`}
              className="flex items-center gap-2 text-navy/80 hover:text-navy text-sm font-medium transition-colors"
              aria-label={t.phone}
            >
              <Phone className="w-4 h-4" />
              {t.phone}
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}